import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../../component/ui/Button';
import InputField from '../../../component/form/FormInput';
import Table from '../../../component/table/Table';
import Pagination from '../../../component/pagination';
import DataNotFound from '../../components/dataNotFound';
import { getListDetailsTableColumns } from './components/ListDetailsTableColumn';
import AddProduct from './AddProduct';
import { toast } from 'react-toastify';
import { getProductListDetails, updateProductList } from '../../../store/action/inventory';
import { product_list } from '../../../store/action/product';
import { getProductBatchList, getProductDetails } from '../../../services/products';
import './List.scss';

const deriveProductTags = (prod) => {
	if (Array.isArray(prod?.tags) && prod.tags.length > 0) return prod.tags;
	if (Array.isArray(prod?.raw?.tags) && prod.raw.tags.length > 0) return prod.raw.tags;
	if (Array.isArray(prod?.product_tags) && prod.product_tags.length > 0) return prod.product_tags;

	const tags = new Set();

	let stockInHand = prod?.stock_in_hand ?? prod?.raw?.stock_in_hand;
	if (stockInHand === undefined || stockInHand === null) {
		const stockStr = prod?.['AVAILABLE STOCK'] || prod?.raw?.['AVAILABLE STOCK'];
		if (typeof stockStr === 'string') {
			const parsed = parseInt(stockStr, 10);
			if (!isNaN(parsed)) stockInHand = parsed;
		}
	}

	const stockNum = Number(stockInHand ?? 0);
	const reorderLevel = Number(prod?.reorder_level || prod?.raw?.reorder_level || prod?.min_stock || 0);
	const maxStock = Number(prod?.max_stock || prod?.raw?.max_stock || 0);

	if (stockNum === 0) {
		tags.add('out_of_stock');
	} else if (reorderLevel > 0 ? stockNum <= reorderLevel : stockNum <= 10) {
		tags.add('low_stock');
	} else if (maxStock > 0 && stockNum >= maxStock) {
		tags.add('excess_stock');
	}

	const batches = prod?.batches || prod?.product_batches || prod?.raw?.batches || [];
	if (Array.isArray(batches) && batches.length > 0) {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		batches.forEach((b) => {
			if (Array.isArray(b?.tags)) {
				b.tags.forEach((t) => tags.add(t));
			}
			const expStr = b?.expiry_date || b?.expiry || b?.exp_date;
			if (expStr) {
				const expDate = new Date(expStr);
				if (!isNaN(expDate.getTime())) {
					expDate.setHours(0, 0, 0, 0);
					if (expDate < today) {
						tags.add('expired');
					} else {
						const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
						if (diffDays <= 30) {
							tags.add('near_expiry');
						}
					}
				}
			}
		});
	}

	if (prod?.status === 0 || prod?.status === '0' || prod?.raw?.status === 0) {
		tags.add('inactive');
	}

	return Array.from(tags);
};

const ListDetails = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const suburl = localStorage.getItem('suburl') || 'inventory';

	const [listName, setListName] = useState('');
	const [description, setDescription] = useState('');
	const [assignedProducts, setAssignedProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isAddProductsOpen, setIsAddProductsOpen] = useState(false);
	const [, setSelectedRows] = useState([]);
	const [sortDirection, setSortDirection] = useState({ PRODUCTNAME: 'asc' });

	// Accordion Batch State
	const [expandedProductIds, setExpandedProductIds] = useState([]);
	const [batchCache, setBatchCache] = useState({});
	const [loadingBatches, setLoadingBatches] = useState({});

	const [params, setParams] = useState(() => {
		const isFresh = sessionStorage.getItem('isFreshListDetails');
		if (isFresh) {
			sessionStorage.removeItem('isFreshListDetails');
			sessionStorage.removeItem('listDetailsParams');
			return {
				page: 1,
				pageSize: 20,
			};
		}
		const savedState = sessionStorage.getItem('listDetailsParams');
		if (savedState) {
			sessionStorage.removeItem('listDetailsParams');
			try {
				return JSON.parse(savedState);
			} catch (e) {}
		}
		return {
			page: Number(searchParams.get('page')) || 1,
			pageSize: Number(searchParams.get('pageSize')) || 20,
		};
	});

	useEffect(() => {
		const currentPage = params.page || 1;
		const currentPageSize = params.pageSize || 20;
		const queryStr = `?page=${currentPage}&pageSize=${currentPageSize}`;
		sessionStorage.setItem(`url_params_${window.location.pathname}`, queryStr);
	}, [params]);

	const totalPages = Math.ceil(assignedProducts.length / params.pageSize) || 1;

	useEffect(() => {
		if (!loading && assignedProducts.length > 0 && params.page > totalPages) {
			setParams((prev) => ({ ...prev, page: totalPages }));
		}
	}, [totalPages, params.page, loading, assignedProducts.length]);

	const paginatedProducts = useMemo(() => {
		const start = (params.page - 1) * params.pageSize;
		return assignedProducts.slice(start, start + params.pageSize);
	}, [assignedProducts, params.page, params.pageSize]);

	const fetchListDetailsData = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		try {
			const [res, allProductsRes] = await Promise.all([
				getProductListDetails(id),
				product_list('page=1&pageSize=2000').catch(() => null),
			]);
			console.log('getProductListDetails Response:', res);

			const allProducts =
				allProductsRes?.data?.productWithStockCount ||
				allProductsRes?.productWithStockCount ||
				allProductsRes?.data?.products ||
				allProductsRes?.products ||
				[];

			const tagsMap = {};
			const statusMap = {};
			allProducts.forEach((p) => {
				const pId = p.product_id || p.id;
				if (pId) {
					if (Array.isArray(p.tags) && p.tags.length > 0) {
						tagsMap[pId] = p.tags;
					}
					if (p.status !== undefined && p.status !== null) {
						statusMap[pId] = p.status;
					}
				}
			});

			const details = res?.data?.details || res?.details || res?.data;
			if (details) {
				setListName(details.name || '');
				setDescription(details.description || '');

				const rawProducts = details.associated_products || details.products || [];
				const mappedProducts = rawProducts.map((prod) => {
					const prodId = prod.product_id || prod.id;
					const initialBatches =
						Array.isArray(prod.batches) && prod.batches.length > 0
							? prod.batches
							: Array.isArray(prod.product_batches) && prod.product_batches.length > 0
							? prod.product_batches
							: null;

					const exactBackendTags = tagsMap[prodId] || prod.tags || prod.raw?.tags;
					const finalTags =
						Array.isArray(exactBackendTags) && exactBackendTags.length > 0
							? exactBackendTags
							: deriveProductTags(prod);

					return {
						id: prodId,
						product_id: prodId,
						PRODUCTNAME: prod.product_name || prod.name || prod.PRODUCTNAME,
						UPC: prod.product_code || prod.UPC || prod.upc || '-',
						CATEGORY: prod.category_name || prod.category || prod.CATEGORY || '-',
						'AVAILABLE STOCK': prod['AVAILABLE STOCK'] || `${prod.stock_in_hand ?? 0} ${prod.unit_name || 'units'}`,
						'UNIT PRICE': prod['UNIT PRICE'] || prod.unit_price || prod.sales_price || '0',
						batches: initialBatches,
						unit_name: prod.unit_name || 'units',
						tags: finalTags,
						status: statusMap[prodId] ?? prod.status ?? prod.raw?.status,
					};
				});
				setAssignedProducts(mappedProducts);
			}
		} catch (err) {
			console.error('Failed to fetch list details:', err);
			toast.error('Failed to load list details');
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		fetchListDetailsData();
	}, [fetchListDetailsData]);

	const handleCloseDetails = () => navigate(`/user/${suburl}/lists`);

	const handleSaveList = async () => {
		if (!id) return;
		try {
			setLoading(true);
			const payload = {
				name: listName.trim(),
				description: description.trim(),
				status: 1,
				product_ids: assignedProducts.map((p) => p.id || p.product_id),
			};
			const res = await updateProductList(id, payload);
			if (res?.success) {
				toast.success(res?.message || 'Product List updated successfully');
				navigate(`/user/${suburl}/lists`);
			} else {
				const isDuplicate = res?.response?.status === 409 || res?.status === 409;
				toast.error(isDuplicate ? 'Product list name already exist' : (res?.response?.data?.message || res?.message || 'Failed to update list'));
			}
		} catch (err) {
			console.error('Update List Error:', err);
			const isDuplicate = err?.response?.status === 409;
			toast.error(isDuplicate ? 'Product list name already exist' : (err?.response?.data?.message || err?.message || 'Failed to update list'));
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveProduct = useCallback((productId) => {
		setAssignedProducts((prev) => prev.filter((item) => item.id !== productId));
		setExpandedProductIds((prev) => prev.filter((idVal) => idVal !== productId));
		setBatchCache((prev) => {
			const next = { ...prev };
			delete next[productId];
			return next;
		});
		toast.success('Product removed from list');
	}, []);

	const handleToggleExpand = useCallback(
		async (productId, e) => {
			if (e) {
				e.stopPropagation();
				e.preventDefault();
			}

			const isExpanded = expandedProductIds.includes(productId);

			if (isExpanded) {
				setExpandedProductIds((prev) => prev.filter((idVal) => idVal !== productId));
				return;
			}

			const product = assignedProducts.find((p) => p.id === productId);
			const cached = batchCache[productId];

			const knownBatches = Array.isArray(cached) ? cached : product?.batches;
			if (Array.isArray(knownBatches) && knownBatches.length === 0) {
				// 0 batches known -> do not expand subrows
				return;
			}

			setExpandedProductIds((prev) => [...prev, productId]);

			const hasCachedBatches = Array.isArray(cached) && cached.length > 0;
			const hasProductBatches = Array.isArray(product?.batches) && product.batches.length > 0;

			if (!hasCachedBatches && !hasProductBatches && !loadingBatches[productId]) {
				try {
					setLoadingBatches((prev) => ({ ...prev, [productId]: true }));
					const targetId = product?.product_id || product?.id || productId;

					let extractedBatches = [];
					try {
						const resData = await getProductBatchList({ product_id: targetId });
						extractedBatches = Array.isArray(resData)
							? resData
							: Array.isArray(resData?.batches)
							? resData.batches
							: Array.isArray(resData?.product_batches)
							? resData.product_batches
							: Array.isArray(resData?.data)
							? resData.data
							: [];
					} catch (batchErr) {
						console.warn('getProductBatchList failed, attempting getProductDetails:', batchErr);
					}

					// Fallback: If getProductBatchList returned empty or errored, fetch full product details
					if (!extractedBatches || extractedBatches.length === 0) {
						try {
							const details = await getProductDetails(targetId);
							if (details) {
								extractedBatches =
									details.product_batch ||
									details.batches ||
									details.product_batches ||
									[];
							}
						} catch (dErr) {
							console.error('getProductDetails fallback failed:', dErr);
						}
					}

					setBatchCache((prev) => ({
						...prev,
						[productId]: extractedBatches,
					}));

					// If no batches found, collapse/do not open subrows
					if (!extractedBatches || extractedBatches.length === 0) {
						setExpandedProductIds((prev) => prev.filter((idVal) => idVal !== productId));
					}
				} catch (err) {
					console.error('Failed to fetch product batches:', err);
					setBatchCache((prev) => ({ ...prev, [productId]: [] }));
					setExpandedProductIds((prev) => prev.filter((idVal) => idVal !== productId));
				} finally {
					setLoadingBatches((prev) => ({ ...prev, [productId]: false }));
				}
			}
		},
		[expandedProductIds, assignedProducts, batchCache, loadingBatches]
	);

	const handleAddProducts = (newProducts) => {
		if (!newProducts?.length) return;
		const formatted = newProducts.map((item, idx) => ({
			id: item.id || Date.now() + idx,
			product_id: item.id || item.raw?.product_id || item.raw?.id,
			PRODUCTNAME: item.name || item.product_name,
			UPC: item.code || item.product_code || item.UPC || '-',
			CATEGORY: item.category_name || item.CATEGORY || '-',
			'AVAILABLE STOCK': `${item.stock_in_hand ?? 0} ${item.unit_name || 'units'}`,
			'UNIT PRICE': item.unit_price || item.sales_price || '0',
			batches: item.batches || null,
			unit_name: item.unit_name || 'units',
			tags: deriveProductTags(item),
			status: item.status ?? item.raw?.status,
		}));
		setAssignedProducts((prev) => [...formatted, ...prev]);
		toast.success('Products added to list successfully');
	};

	const handleSort = (fieldKey) => {
		const currentSort = sortDirection[fieldKey] === 'asc' ? 'desc' : 'asc';
		setSortDirection({ [fieldKey]: currentSort });
		setAssignedProducts((prev) =>
			[...prev].sort((a, b) => {
				let valA = a[fieldKey] ?? '';
				let valB = b[fieldKey] ?? '';
				if (typeof valA === 'number' && typeof valB === 'number') {
					return currentSort === 'asc' ? valA - valB : valB - valA;
				}
				valA = String(valA).toLowerCase();
				valB = String(valB).toLowerCase();
				if (valA < valB) return currentSort === 'asc' ? -1 : 1;
				if (valA > valB) return currentSort === 'asc' ? 1 : -1;
				return 0;
			})
		);
	};

	const displayRows = useMemo(() => {
		return paginatedProducts.flatMap((product) => {
			const productId = product.id;
			const isOpen = expandedProductIds.includes(productId);

			const cached = batchCache[productId];
			const batches = Array.isArray(cached) ? cached : product.batches;
			const hasNoBatches = Array.isArray(batches) && batches.length === 0;

			const parentRow = {
				...product,
				isBatch: false,
				isOpen,
				hasNoBatches,
				batches,
			};

			if (!isOpen) {
				return [parentRow];
			}

			const isLoading = loadingBatches[productId];

			if (isLoading || !batches || batches.length === 0) {
				return [parentRow];
			}

			const batchRows = batches.map((batch, idx) => {
				const batchNo = batch.batch_no || batch.value || batch.batch_number || `Batch ${idx + 1}`;
				const batchQty = batch.quantity ?? batch.qty ?? batch.stock_in_hand ?? 0;
				const batchPrice = batch.unit_price || batch.sales_price || batch.mrp || '0';

				let stockFormatted = `${batchQty} units`;
				if (typeof product['AVAILABLE STOCK'] === 'string' && product['AVAILABLE STOCK'].includes(' ')) {
					const parts = product['AVAILABLE STOCK'].split(' ');
					stockFormatted = `${batchQty} ${parts.slice(1).join(' ')}`;
				} else if (product.unit_name) {
					stockFormatted = `${batchQty} ${product.unit_name}`;
				}

				return {
					id: `batch-${productId}-${batch.product_batch_id || batch.id || idx}`,
					isBatch: true,
					batch_no: batchNo,
					PRODUCTNAME: batchNo,
					UPC: product.UPC || '-',
					CATEGORY: product.CATEGORY || '-',
					'AVAILABLE STOCK': stockFormatted,
					'UNIT PRICE': batchPrice,
					parentId: productId,
				};
			});

			return [parentRow, ...batchRows];
		});
	}, [paginatedProducts, expandedProductIds, batchCache, loadingBatches]);

	const handleProductClick = useCallback(
		(productId) => {
			if (productId) {
				sessionStorage.setItem('listDetailsParams', JSON.stringify(params));
				navigate(`/user/${suburl}/product-create/${productId}`);
			}
		},
		[navigate, suburl, params]
	);

	const columns = useMemo(
		() => getListDetailsTableColumns(handleSort, sortDirection, handleRemoveProduct, expandedProductIds, handleToggleExpand, handleProductClick),
		[handleSort, sortDirection, handleRemoveProduct, expandedProductIds, handleToggleExpand, handleProductClick]
	);

	return (
		<div className="list-details-page-wrapper d-flex flex-column h-100 overflow-hidden" data-testid="product-list-detail-page">
			{/* Top Header Bar */}
			<div className="product-pg-header add-product">
				<p data-testid="product-list-detail-title">List Details</p>
				<div className="add-product-button">
					<Button
						type="button"
						button="Save"
						className="btn-primary mt-0 px-4"
						onClick={handleSaveList}
						dataTestId="product-list-detail-save-button"
					/>
					<Button
						type="button"
						className="mt-0 btn-danger add-pro"
						button=""
						close
						onClick={handleCloseDetails}
						dataTestId="product-list-detail-close-button"
					/>
				</div>
			</div>

			{/* Metadata & Controls Section */}
			<div className="px-3">
				<div className="row g-3 mb-3">
					<div className="col-md-3 col-sm-6">
						<InputField
							label="List Name"
							type="text"
							name="listName"
							value={listName}
							onChange={(val) => setListName(typeof val === 'string' ? val : val?.target?.value || '')}
							placeholder="Enter List Name"
							dataTestId="product-list-detail-name-input"
						/>
					</div>
					<div className="col-md-4 col-sm-6">
						<InputField
							label="Description"
							type="text"
							name="description"
							value={description}
							onChange={(val) => setDescription(typeof val === 'string' ? val : val?.target?.value || '')}
							placeholder="Enter Description"
							dataTestId="product-list-detail-description-input"
						/>
					</div>
				</div>

				<div className="d-flex justify-content-between align-items-center mb-2">
					<h5 className="fw-bold mb-0 text-dark">
						Assigned Products
					</h5>
					<Button
						add
						type="button"
						button="Add Products"
						className="btn-primary px-3 mt-0"
						onClick={() => setIsAddProductsOpen(true)}
						dataTestId="product-list-detail-add-products-button"
					/>
				</div>
			</div>

			{/* Full Width Edge-to-Edge Table Content */}
			{loading ? (
				<div className="d-flex justify-content-center align-items-center flex-grow-1" data-testid="product-list-detail-loading-state">
					<span className="Page_loader" role="status" aria-hidden="true"></span>
				</div>
			) : assignedProducts.length > 0 ? (
				<div className="flex-grow-1 d-flex flex-column w-100" data-testid="product-list-detail-table">
					<div className="productTable-body flex-grow-1">
						<Table
							row={displayRows}
							coloum={columns}
							className="productTable list-details-table"
							isMultiSelect={true}
							action={setSelectedRows}
						/>
					</div>
					<Pagination
						currentPage={params.page}
						totalPages={totalPages}
						onChangePage={(newPage) => setParams((prev) => ({ ...prev, page: newPage }))}
						pageSize={params.pageSize}
						setParam={(value) => setParams((prev) => ({ ...prev, pageSize: value, page: 1 }))}
					/>
				</div>
			) : (
				<div className="px-3" data-testid="product-list-detail-empty-state">
					<DataNotFound
						title={'No assigned products found!'}
						subtitle={'Add products to this list to organize your inventory.'}
						descriptionTitle={'Keep your inventory organized!'}
						descriptionSubTitle={'Assign products to this list easily.'}
						buttonText={'Add Products'}
						onClick={() => setIsAddProductsOpen(true)}
					/>
				</div>
			)}

			{/* Add Products Modal */}
			{isAddProductsOpen && (
				<AddProduct
					isOpen={isAddProductsOpen}
					onClose={() => setIsAddProductsOpen(false)}
					onAddProducts={handleAddProducts}
					assignedProductIds={assignedProducts.map((p) => p.id)}
				/>
			)}
		</div>
	);
};

export default ListDetails;
